import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { IAgTechAppContext, useAppContext } from "../../app/AgTechAppContext";
import { AgTechApiError } from "../../data/http/AgTechCoreApiRequests";
import { AgTechMessage } from "../../functions/AgTechAppMessaging";
import { logAction, logDevError, logEvent } from "../../logging/AgTechLogger";
import { agtechDelay } from "../../utilities/AgTechUtilities";
import React, { useRef } from "react";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { AgTechPopUpContext, useAgTechPopUpContext } from "../../functions/AgTechAppPopUps";
import { AgTechQuery, AgTechQueryBuilder, AgTechQueryInput, AgTechQueryProp, AgTechQueryRun, useAgTechQuery, waitOnQuery } from "../queries/AgTechDataQueries";
import { AgTechHttpRequester, useAgTechHttpRequester } from "../http/AgTechHttp";
import { useAgTechHttpRequestExecutor } from "./AgTechHttpRequests";

export interface IConvertToActionData<TSourceData, TActionData> {
    convertToActionData: (sourceData: TSourceData) => TActionData
}

export type AgTechDataAction<TInput, TOutput, TContextData = TInput> = {
    name: string,
    query?: AgTechQueryRun<TContextData>,
    getConfiguration?: (props: AgTechDataActionGetActionConfigurationProps<TInput>) => AgTechDataActionConfiguration,
    validate?: (props: AgTechDataActionValidationProps<TInput, TContextData>) => Promise<void>,
    confirm?: (props: AgTechDataActionConfirmationProps<TInput, TContextData>) => Promise<boolean>,
    action: (props: AgTechDataActionGetDataActionProps<TInput, TContextData>) => Promise<AgTechDataActionExecutionResult<TOutput>> | Promise<AgTechDataActionExecutionResult<TOutput>>,
    onSuccess?: (props: AgTechDataActionSuccessfulExecutionHandlingProps<TInput, TOutput>) => Promise<void>,
    onFailure?: (failureHandlerProps: AgTechDataActionFailedExecutionHandlingProps<TInput>) => Promise<void>,
    isBackgroundAction?: boolean,
    customizations?: AgTechDataActionCustomizations<TInput, TOutput, TContextData>
}

export type AgTechDataActionCustomizations<TInput, TOutput, TContextData = TInput> = {
    onConfirm?: (props: AgTechDataActionConfirmationProps<TInput, TContextData>) => AgTechDataActionConfirmationProps<TInput, TContextData>
}

export type AgTechCoreDataActionProps<TInput> = {
    originalEntity?: TInput,
    submittedEntity: TInput,
    actionLoggingContext?: AgTechDataActionLoggingContext<TInput, any>,
}

export type AgTechCoreDataActionHandlers<TOutput> = {
    onActionExecuted?: (result: AgTechDataActionExecutionResult<TOutput>) => Promise<any>
}

/// Context

export type DataActionContextProps =  & {
    actionConfiguration: AgTechDataActionConfiguration
}

export type AgTechDataActionContext<TInput, TOutput> = {
    actionName: string,
    onExecutionStarted: (props: DataActionContextProps & AgTechCoreDataActionProps<TInput>) => Promise<void>,
    onExecutionSuccessful: (props: DataActionContextProps & AgTechDataActionExecutionResult<TInput> & { 
        actionData: TInput,
        responseData: TOutput
    }) => Promise<void>,
    onExecutionCompleted: (result: DataActionContextProps & AgTechDataActionExecutionResult<TOutput>) => Promise<void>,
    onExecutionFailed: (props: DataActionContextProps) => Promise<void>
}

export type AgTechDataActionEventHandlers<TInput, TOutput> = {
    onExecutionStarted?: (props: DataActionContextProps & AgTechCoreDataActionProps<TInput>) => Promise<void>,
    onExecutionSuccessful?: (props: DataActionContextProps & AgTechDataActionExecutionResult<TInput> & { 
        actionData: TInput,
        responseData: TOutput
    }) => Promise<void>,
    onExecutionCompleted?: (result: DataActionContextProps & AgTechDataActionExecutionResult<TOutput>) => Promise<void>,
    onExecutionFailed?: (props: DataActionContextProps) => Promise<void>
}

export const DataActionContext = React.createContext<AgTechDataActionContext<any, any>>({
    actionName: '',
    onExecutionStarted: async() => {
        console.error('No action execution started handler provided');
    },
    onExecutionSuccessful: async() => {
        console.error('No action execution started handler provided');
    },
    onExecutionCompleted: async() => {
        console.error('No action execution started handler provided');
    },
    onExecutionFailed: async() => {
        console.error('No action execution started handler provided');
    },
});

export const useDataActionContext = <TInput, TOutput>() => useContext<AgTechDataActionContext<TInput, TOutput>>(DataActionContext);

/// Configuration

export type AgTechDataActionGetActionConfigurationProps<TEntity> = AgTechCoreDataActionProps<TEntity>;

export type AgTechDataActionConfiguration = {
    actionExecutionMessage?: string,
    actionExecutionDelay?: number,
    showConfirmationMessage?: boolean, // Will default to true if not provided
    actionConfirmationMessage?: string,
    actionConfirmationDetails?: string,
    actionConfirmationDelay?: number,
    shouldLogExecution?: boolean
}

export const DefaultAgTechDataActionConfiguration: AgTechDataActionConfiguration = {
    actionExecutionMessage: ''
}

/// Validation

export type AgTechDataActionValidationProps<TEntity, TContextData> = AgTechCoreDataActionProps<TEntity> & {
    validation: AgTechActionValidationResult<TEntity>,
    context: TContextData
}

export class AgTechActionValidationResult<TInput> {
    appContext: IAgTechAppContext;
    wasSuccessful: boolean;
    correctedEntity: TInput;
    errorMessages: (string | AgTechMessage)[];

    constructor(appContext: IAgTechAppContext, submittedEntity: TInput) {
        this.appContext = appContext;
        this.wasSuccessful = true;
        this.correctedEntity = submittedEntity;
        this.errorMessages = [];
    }

    public failWithWarning = (message: string | AgTechMessage, correctEntity?: (currentEntity: TInput) => TInput) => {
        this.appContext.messaging.showWarning(message);

        if (correctEntity) {
            let correctedEntity = correctEntity(this.correctedEntity);
            this.correctedEntity = { ...correctedEntity };
        }

        this.wasSuccessful = false;
        this.errorMessages.push(message);
    }

    public failWithWarningIf = (condition: boolean, message: string | AgTechMessage, correctEntity?: (currentEntity: TInput) => TInput) => {
        if (condition) {
            return this.failWithWarning(message, correctEntity);
        }
    }
}

/// Confirmation

export type AgTechDataActionConfirmationProps<TEntity, TContextData> = AgTechCoreDataActionProps<TEntity> & {
    appContext: IAgTechAppContext
};

/// Execution

export type AgTechDataActionGetDataActionProps<TEntity, TContextData> = {
    http: AgTechHttpRequester,
    originalEntity?: TEntity,
    submittedEntity: TEntity,
    actionLoggingContext: AgTechDataActionLoggingContext<any, any>,
    context: TContextData
};

export type AgTechDataActionSuccess<TEntity> = {
    data: TEntity
}

export type AgTechDataActionExecutionResult<TEntity> = {
    success?: AgTechDataActionSuccess<TEntity>,
    errorMessage?: string,
    // Mirrors AgTechApiError.errorCode - see that type for what it carries.
    errorCode?: number,
    wasCancelled?: boolean
}

export class DataActionResults {
    static Success = <TData, >(data: TData): AgTechDataActionExecutionResult<TData> => {
        return {
            success: {
                data: data
            }
        }
    }

    static Failed = (error: any): AgTechDataActionExecutionResult<any> => {
        return {
            errorMessage: error?.toString(),
            errorCode: error?.errorCode
        }
    }
}

export type AgTechUpdateQueryDataProps<TQueryInputData, TQueryResultData> = {
    query: AgTechQuery<TQueryInputData, TQueryResultData>,
    update: (currData: TQueryResultData) => TQueryResultData
}
& AgTechQueryInput<TQueryInputData>;

export type AgTechInvalidateQueryDataProps<TQueryInputData> = {
    query: AgTechQuery<TQueryInputData, any>,
}
& AgTechQueryInput<TQueryInputData>;

export type AgTechDataActionSuccessfulExecutionHandlingProps<TActionInputType, TActionResponseType> = AgTechCoreDataActionProps<TActionInputType> & {
    responseData: TActionResponseType,
    appContext: IAgTechAppContext,
    actionExecutor: AgTechDataActionExecutor,
    queryClient: QueryClient,
    actionLoggingContext: AgTechDataActionLoggingContext<TActionInputType, TActionResponseType>,
    executeReducerAction: <TData>(action: ActionCreatorWithPayload<TData>, data: TData) => void,
    updateQueryData: <TQueryInputData, TQueryResultData>(props: AgTechUpdateQueryDataProps<TQueryInputData, TQueryResultData>) => void,
    invalidateQuery: <TQueryInputData, >(props: AgTechInvalidateQueryDataProps<TQueryInputData>) => void,
    closePopUp: () => void
}

export type AgTechDataActionFailedExecutionHandlingProps<TEntity> = {
    appContext: IAgTechAppContext,
    error: AgTechApiError,
    showError: (errorMessage: string, errorDetails?: string) => void,
    submittedEntity: TEntity
}

export const useDefaultAgTechDataAction = (
    name: string,
    configuration?: AgTechDataActionConfiguration
): AgTechDataAction<any, any, any> => {
    return {
        name: name,
        getConfiguration: () => configuration ?? DefaultAgTechDataActionConfiguration,
        action: async () => {
            return DataActionResults.Success({});
        }
    }
}

/// Action Execution Handling

export type AgTechDataActionExecutionProps<TInput, TOutput, TContextData = TInput> = {
    action: AgTechDataAction<TInput, TOutput, TContextData>,
    executionInputProps: AgTechCoreDataActionProps<TInput>,
    executionConfiguration: AgTechDataActionExecutionConfigurationProps<TInput, TOutput>
}

export type AgTechDataActionStepExecutionProps<TActionInput, TActionOutput, TNextStepOutput> = AgTechCoreDataActionProps<TActionInput> & AgTechDataActionExecutionConfigurationProps<TActionInput, TActionOutput> & {
    onSuccess: (output: TActionOutput) => Promise<AgTechDataActionExecutionResult<TNextStepOutput>>
}

export type AgTechDataActionExecutionConfigurationProps<TInput, TOutput> = {
    handlers?: AgTechCoreDataActionHandlers<TOutput>,
    settings?: AgTechDataActionExecutionSettings
}

export type AgTechDataActionExecutionSettings = {
    shouldConfirmExecution?: boolean
}

export type AgTechDataActionExecutionContextProps = {
    appContext: IAgTechAppContext,
    dispatch: Dispatch,
    dataActionContext?: AgTechDataActionContext<any, any>,
    queryClient: QueryClient,
    popupContext?: AgTechPopUpContext,
    http: AgTechHttpRequester
}

export const useDataActionExecutionContextProps = (): AgTechDataActionExecutionContextProps => {
    const loggingContext = useRef({});

    const appContext = useAppContext();
    const dispatch = useDispatch();
    const dataActionContext = useDataActionContext();
    const queryClient = useQueryClient();
    const popupContext = useAgTechPopUpContext();
    const httpExecutor = useAgTechHttpRequester(loggingContext);

    return {
        appContext,
        dispatch,
        dataActionContext: dataActionContext,
        queryClient,
        popupContext,
        http: httpExecutor
    }; 
}

export type AgTechDataActionExecutor = {
    executeAction: <TInput, TOutput, TContextData>(
        action: AgTechDataAction<TInput, TOutput, TContextData>,
        props: AgTechCoreDataActionProps<TInput> & AgTechDataActionExecutionConfigurationProps<TInput, TOutput>,
    ) => Promise<AgTechDataActionExecutionResult<TOutput>>,
    executeStep: <TActionInput, TActionOutput, TNextStepOutput, TActionContextData>(
        actionStep: AgTechDataAction<TActionInput, TActionOutput, TActionContextData>,
        props: AgTechDataActionStepExecutionProps<TActionInput, TActionOutput, TNextStepOutput>
    ) => Promise<AgTechDataActionExecutionResult<TNextStepOutput>>,
    validateAction: <TInput, TOutput, TContextData>(
        action: AgTechDataAction<TInput, TOutput, TContextData>,
        props: AgTechCoreDataActionProps<TInput> & AgTechDataActionExecutionConfigurationProps<TInput, TOutput>
    ) => Promise<AgTechActionValidationResult<TInput>>
}

export const useDataActionExecutor = (): AgTechDataActionExecutor => {
    const dataActionExecutionProps = useDataActionExecutionContextProps();
    return buildDataActionExecutor(dataActionExecutionProps);
}

export const buildDataActionExecutor = <TInput, TOutput>(executionContextProps: AgTechDataActionExecutionContextProps): AgTechDataActionExecutor => {
    return {
        executeAction: async (action, props) => {
            return await executeAgTechDataAction({
                action: action,
                executionInputProps: props,
                executionConfiguration: { ...props },
                executionContextProps: executionContextProps,
            });
        },
        validateAction: async (action, props) => {
            let actionConfiguration = await getActionConfiguration({
                ...props,
                dataAction: action
            });

            let actionLoggingContext = getActionLoggingContext({
                ...props,
                dataAction: action
            });

            const contextData = await getActionContextData({
                action: action,
                submittedEntity: props.submittedEntity
            });

            return await validateAction({
                action: action,
                executionInputProps: props,
                executionConfiguration: { ...props },
                executionContextProps: executionContextProps,
                dataActionConfiguration: actionConfiguration,
                dataActionLoggingContext: actionLoggingContext
            },
            contextData);
        },
        executeStep: async (action, props) => {
            let initialActionExecutionResult = await executeAgTechDataAction({
                action: action,
                executionInputProps: props,
                executionConfiguration: { ...props },
                executionContextProps: executionContextProps,
            });

            if (initialActionExecutionResult.success) {
                return await props.onSuccess(initialActionExecutionResult.success.data);
            }
            else {
                return {
                    success: undefined
                }
            }
        }
    }
}

export type AgTechDataActionLoggingContext<TInput, TOutput> = {
    name: string,
    execution: {
        inputProps: AgTechCoreDataActionProps<TInput>,
        result: {
            wasActionExecuted: boolean,
            wasActionSuccessful: boolean,
            resultData?: TOutput
        },
        validation?: {
            wasValid: boolean,
            validationErrors?: (string | AgTechMessage)[],
            correctedEntity?: TInput
        },
        confirmation?: {
            wasConfirmed: boolean
        },
        requests?: {
            [request: string]: {
                type: string,
                url: string,
                postedData?: any,
                responseData?: any,
                wasSuccessful: boolean
            }
        }
        childActions?: { [name: string]: AgTechDataActionLoggingContext<any, any> },
        messages: []
    }
    onSuccess?: {
        dataSentToSuccessHandler: TOutput,
        childActions?: AgTechDataActionLoggingContext<any, any>[],
        messages?: [],
        reducerActions: { [action: string]: { action: string, payload: any } },
        queryUpdates: { key: any[], originalData: any, updatedData: any }[]
    },
}

const getActionContextData = async <TInput, TOutput, TContextData = TInput>(props: {
    action: AgTechDataAction<TInput, TOutput, TContextData>,
    submittedEntity: TInput
}) => {
    const contextData = props.action.query
        ? await waitOnQuery(props.action.query)
        : props.submittedEntity;

    return contextData as TContextData;
}

const executeAgTechDataAction = async <TInput, TOutput, TContextData = TInput>(
    props: AgTechDataActionExecutionProps<TInput, TOutput, TContextData> & {
    executionContextProps: AgTechDataActionExecutionContextProps
}) => {
    let executionResult: AgTechDataActionExecutionResult<TOutput> = {
        success: undefined
    };

    const contextData = await getActionContextData({
        action: props.action,
        submittedEntity: props.executionInputProps.submittedEntity
    });

    const dataActionLoggingContext = getActionLoggingContext({
        ...props.executionInputProps,
        dataAction: props.action
    });

    const actionConfiguration = await getActionConfiguration({
        ...props.executionInputProps,
        dataAction: props.action
    });

    const actionExecutionHelperProps: DataActionExecutorHelperProps<TInput, TOutput, TContextData> = {
        ...props,
        dataActionConfiguration: actionConfiguration,
        dataActionLoggingContext: dataActionLoggingContext,
        executionContextProps: {
            ...props.executionContextProps,
            dataActionContext: props.executionContextProps.dataActionContext?.actionName === props.action.name
                ? props.executionContextProps.dataActionContext
                : undefined
        }
    }

    try
    {
        const validationResult = await validateAction(
            actionExecutionHelperProps,
            contextData as TContextData);
    
        if (validationResult && validationResult.wasSuccessful) {       
            const shouldConfirmAction = props.executionConfiguration.settings?.shouldConfirmExecution ?? true;
            const shouldExecuteAction = shouldConfirmAction ? await confirmAction(actionExecutionHelperProps): true;
    
            if (shouldExecuteAction) {
                executionResult = await executeAction(
                    actionExecutionHelperProps,
                    contextData as TContextData
                );
    
                if (props.executionConfiguration.handlers?.onActionExecuted) {
                    await props.executionConfiguration.handlers.onActionExecuted(executionResult);
                }
    
                dataActionLoggingContext.execution.result = {
                    wasActionExecuted: !(executionResult.wasCancelled ?? false),
                    wasActionSuccessful: executionResult.success !== undefined,
                    resultData: executionResult.success?.data
                };
    
                if (executionResult.success) {
                    await handleSuccessfulActionExecution({
                        ...actionExecutionHelperProps,
                        executionResultData: executionResult.success.data
                    });
                }
                else {
                    await handleFailedActionExecution({
                        ...actionExecutionHelperProps,
                        executionResult
                    });
                }
            }
            else {
                executionResult.wasCancelled = true;
            }
        }
    }
    catch (e) {
        logDevError(e);
    }
    finally {
        if (actionConfiguration.shouldLogExecution ?? true) {
            if (props.executionInputProps.actionLoggingContext) {
                props.executionInputProps.actionLoggingContext.execution.childActions =
                    props.executionInputProps.actionLoggingContext.execution.childActions ?? {};
    
                props.executionInputProps.actionLoggingContext.execution.childActions[dataActionLoggingContext.name] = {
                    ...dataActionLoggingContext
                };
            }
            else {
                logAction(dataActionLoggingContext);
            }
        }
    }

    

    return executionResult;
}

/// Internal Action Helpers

declare type DataActionExecutorHelperProps<TInput, TOutput, TContextData = TInput> = 
    AgTechDataActionExecutionProps<TInput, TOutput, TContextData> & {
    executionContextProps: AgTechDataActionExecutionContextProps,
    dataActionConfiguration: AgTechDataActionConfiguration,
    dataActionLoggingContext: AgTechDataActionLoggingContext<TInput, TOutput>,
}

export const getActionConfiguration = async <TInput, TOutput, TContextData = TInput>(
    props: AgTechCoreDataActionProps<TInput> & {
    dataAction: AgTechDataAction<TInput, TOutput, TContextData>
}
) => {
    return props.dataAction.getConfiguration
        ? props.dataAction.getConfiguration({
            ...props
        })
        : DefaultAgTechDataActionConfiguration;
}

export const getActionLoggingContext = <TInput, TOutput, TContextData = TInput>(props: AgTechCoreDataActionProps<TInput> & {
    dataAction: AgTechDataAction<TInput, TOutput, TContextData>
}): AgTechDataActionLoggingContext<TInput, TOutput> => {
    return {
        name: props.dataAction.name ?? 'N/A',
        execution: {
            inputProps: {
                originalEntity: props.originalEntity,
                submittedEntity: props.submittedEntity
            },
            result: {
                wasActionExecuted: false,
                wasActionSuccessful: false
            },
            messages: []
        }
    };
}

const validateAction = async <TInput, TOutput, TContextData = TInput>(
    props: DataActionExecutorHelperProps<TInput, TOutput, TContextData>,
    contextData: TContextData
) => {
    let wasValidationPerformed = false;

    let actionValidationResult: AgTechActionValidationResult<TInput> = new AgTechActionValidationResult<TInput>(
        props.executionContextProps.appContext,
        props.executionInputProps.submittedEntity
    );

    if (props.action.validate) {
        wasValidationPerformed = true;

        await props.action.validate({
            ...props.executionInputProps,
            validation: actionValidationResult,
            context: contextData as TContextData
        });
    }

    if (wasValidationPerformed) {
        props.dataActionLoggingContext.execution.validation = {
            wasValid: actionValidationResult.wasSuccessful,
            validationErrors: actionValidationResult.errorMessages,
            correctedEntity: actionValidationResult.correctedEntity
        };
    }

    return actionValidationResult;
}

const confirmAction = async <TInput, TOutput, TContextData>(
    props: DataActionExecutorHelperProps<TInput, TOutput, TContextData>
) => {
    let wasActionConfirmed = true;

    if (props.action.confirm) {
        const contextData = props.action.query
            ? await waitOnQuery(props.action.query)
            : props.executionInputProps.submittedEntity;

        const confirmationProps = {
            ...props.executionInputProps,
            appContext: props.executionContextProps.appContext,
            context: contextData as TContextData
        };

        const customConfirmationProps = props.action.customizations?.onConfirm
            ? props.action.customizations.onConfirm(confirmationProps)
            : confirmationProps;

        wasActionConfirmed = await props.action.confirm(customConfirmationProps);

        props.dataActionLoggingContext.execution.confirmation = {
            wasConfirmed: wasActionConfirmed
        };
    }

    return wasActionConfirmed;
}

const executeAction = async <TInput, TOutput, TContextData>(
    props: DataActionExecutorHelperProps<TInput, TOutput, TContextData>,
    contextData: TContextData
) => {   
    let actionExecutionResult: AgTechDataActionExecutionResult<TOutput> = {
        success: undefined
    };

    const isBackgroundAction = props.action.isBackgroundAction ?? false;

    try
    {
        if (!isBackgroundAction && props.executionContextProps.dataActionContext?.onExecutionStarted) {
            await props.executionContextProps.dataActionContext.onExecutionStarted({
                actionConfiguration: props.dataActionConfiguration,
                ...props.executionInputProps
            });
        }

        actionExecutionResult = await props.action.action({
            http: props.executionContextProps.http,
            ...props.executionInputProps,
            actionLoggingContext: props.dataActionLoggingContext,
            context: contextData
        });

        if (props.dataActionConfiguration.actionConfirmationDelay) {
            await agtechDelay(props.dataActionConfiguration.actionConfirmationDelay);
        }
    }
    catch (e) {
        logDevError('[Action Failed]', e);

        props.executionContextProps.appContext.messaging.showError({
            header: 'An error occurred',
            details: JSON.stringify({
                action: props.dataActionLoggingContext,
                http: props.executionContextProps.http.loggingContext
            }, null, 4)
        });
        
        actionExecutionResult = {
            success: undefined,
            errorMessage: 'An error occurred'
        }
    };

    return actionExecutionResult;
}

const handleSuccessfulActionExecution = async <TInput, TOutput, TContextData = TInput>(
    props: DataActionExecutorHelperProps<TInput, TOutput, TContextData> & {
        executionResultData: TOutput,
    }
) => {
    let isBackgroundAction = props.action.isBackgroundAction ?? false;

    if (!isBackgroundAction && props.executionContextProps.dataActionContext?.onExecutionSuccessful) {
        await props.executionContextProps.dataActionContext.onExecutionSuccessful({
            actionConfiguration: props.dataActionConfiguration,
            actionData: props.executionInputProps.submittedEntity,
            responseData: props.executionResultData
        });
    }

    if (props.action.onSuccess) {
        props.dataActionLoggingContext.onSuccess = {
            dataSentToSuccessHandler: props.executionResultData,
            reducerActions: {},
            queryUpdates: []
        }

        await props.action.onSuccess({
            ...props.executionContextProps,
            responseData: props.executionResultData,
            originalEntity: props.executionInputProps.originalEntity,
            submittedEntity: props.executionInputProps.submittedEntity,
            actionExecutor: buildDataActionExecutor({
                ...props.executionContextProps
            }),
            actionLoggingContext: props.dataActionLoggingContext,
            executeReducerAction: (action, data) => {
                let reducerActionName = action.toString();

                if (props.dataActionLoggingContext.onSuccess) {
                    props.dataActionLoggingContext.onSuccess.reducerActions[reducerActionName] = {
                        action: reducerActionName,
                        payload: data
                    };
                }

                props.executionContextProps.dispatch(action(data));
            },
            updateQueryData: <TQueryInputData, TQueryResponseData>(
                queryUpdateProps: AgTechUpdateQueryDataProps<TQueryInputData, TQueryResponseData>
            ) => {
                const queryKey = [...queryUpdateProps.query.queryKey(queryUpdateProps.queryContext)];

                props.executionContextProps.queryClient.setQueryData(queryKey, (currData: TQueryResponseData) => {
                    if (currData) {
                        const updatedData = queryUpdateProps.update(currData);

                        if (props.dataActionLoggingContext.onSuccess) {
                            props.dataActionLoggingContext.onSuccess.queryUpdates.push({
                                key: queryKey,
                                originalData: currData,
                                updatedData: updatedData
                            });
                        }

                        return updatedData;
                    }

                    return currData;
                });
            },
            invalidateQuery: <TQueryInputData>(
                queryInvalidateProps: AgTechInvalidateQueryDataProps<TQueryInputData>
            ) => {
                const queryKey = [...queryInvalidateProps.query.queryKey(queryInvalidateProps.queryContext)];

                props.executionContextProps.queryClient.invalidateQueries({
                    queryKey: queryKey
                });
            },
            closePopUp: () => props.executionContextProps.popupContext?.closePopUp()
        });
    }

    if (!isBackgroundAction && props.executionContextProps.dataActionContext?.onExecutionCompleted) {
        await props.executionContextProps.dataActionContext.onExecutionCompleted({
            actionConfiguration: props.dataActionConfiguration,
            success: {
                data: props.executionResultData
            }
        });
    }
}

const handleFailedActionExecution = async <TInput, TOutput, TContextData = TInput>(
    props: DataActionExecutorHelperProps<TInput, TOutput, TContextData> & 
    { executionResult: AgTechDataActionExecutionResult<TOutput> }
) => {
    let isBackgroundAction = props.action.isBackgroundAction ?? false;

    if (!isBackgroundAction && props.executionContextProps.dataActionContext?.onExecutionFailed) {
        await props.executionContextProps.dataActionContext.onExecutionFailed({
            actionConfiguration: props.dataActionConfiguration,
            ...props.executionInputProps
        });
    }

    if (props.action.onFailure) {
        await props.action.onFailure({
            appContext: props.executionContextProps.appContext,
            error: {
                message: props.executionResult.errorMessage ?? 'An error occurred',
                statusCode: 400,
                errorCode: props.executionResult.errorCode
            },
            showError: (message, details) => props.executionContextProps.appContext.messaging.showError({
                header: message,
                details
            }),
            submittedEntity: props.executionInputProps.submittedEntity
        })
    }
}

export const executeReducerAction = (func: () => void) => {
    try
    {
        func();
    }
    catch (e) {
        logEvent('ReducerActionError', {
            exception: e
        });
    }
}

export const useAgTechContextAwareAction = <TInput, TOutput, TContextData = TInput>(action: AgTechDataAction<TInput, TOutput, TContextData>) => {
    
}

/// Actions

// export type AgTechActionDefinition<TActionData, TResponseData, TActionInputData = any, TQueryInputData = any, TQueryContextData = any> = {
//     name: string,
//     query?: (input: TActionInputData) => AgTechQueryProp<TQueryInputData, TQueryContextData>,
//     getConfiguration?: (props: AgTechDataActionGetActionConfigurationProps<TActionData>) => AgTechDataActionConfiguration,
//     validate?: (props: AgTechDataActionValidationProps<TActionData, TQueryContextData>) => Promise<void>,
//     confirm?: (props: AgTechDataActionConfirmationProps<TActionData, TQueryContextData>) => Promise<boolean>,
//     action: (props: AgTechDataActionGetDataActionProps<TActionData, TQueryContextData>) => Promise<AgTechDataActionExecutionResult<TResponseData>> | Promise<AgTechDataActionExecutionResult<TOutput>>,
//     onSuccess?: (props: AgTechDataActionSuccessfulExecutionHandlingProps<TActionData, TResponseData>) => Promise<void>,
//     onFailure?: (failureHandlerProps: AgTechDataActionFailedExecutionHandlingProps<TActionData>) => Promise<void>,
//     isBackgroundAction?: boolean,
//     customizations?: AgTechDataActionCustomizations<TActionData, TResponseData, TQueryContextData>
// }

// export type AgTechActionProp<TActionData, TResponseData, TActionInputData = any, TQueryInputData = any, TQueryContextData = any> = {
//     action: AgTechActionDefinition<TActionData, TResponseData, TActionInputData, TQueryInputData, TQueryContextData>
// } & AgTechActionInput<TActionInputData>;

// export type AgTechActionInput<TActionInputData> = 0 extends (1 & TActionInputData)
//     ? Partial<{ context: any }>
//     : { context: TActionInputData };

// export const useAgTechDataAction = <TInput, TActionData, TResponse>(
//     props: AgTechActionProp<TInput, TActionData, TResponse> &
//     {
//         settings?: {
//             onSuccess?: (props: AgTechDataActionSuccessfulExecutionHandlingProps<TActionData, TResponse>) => Promise<void>,
//         }
//     }
// ): AgTechDataAction<TActionData, TResponse, TInput> => {
//     const actionQuery: AgTechQueryProp<TInput,  = props.action.query
//         ? props.action.query(props.context)
//         : { query: AgTechQueryBuilder.buildQueryResult({ data: {} })};

//     const contextQuery = useAgTechQuery(actionQuery);

//     return {
//         ...props.action,
//         query: contextQuery as any
//     }
// }